import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  FiArrowLeft,
  FiAlertCircle,
  FiClock,
  FiCheckCircle,
  FiXCircle,
  FiEdit3,
  FiSave,
  FiX,
  FiFileText,
  FiImage,
  FiUser,
  FiHash,
  FiCalendar,
  FiDollarSign,
  FiActivity,
  FiMapPin,
  FiFilter,
  FiChevronDown,
  FiChevronUp,
  FiExternalLink,
} from 'react-icons/fi';
import { MdOutlineTimeline } from 'react-icons/md';

import useGetRiwayatPengajuan from '../../hooks/useGetRiwayatPengajuan';
import useUpdateRiwayatPengajuan from '../../hooks/useUpdateRiwayat_pengajuan';
import endpointsAdmin from '../../Services/endpointAdmin';
import api, { buildFileUrl } from '../../Services/api';
import {
  findFieldDeep,
  extractArrayPayload,
  getAdminLembagaScope,
  matchItemToAdminScope,
} from './adminScope';

/* ─── Helpers ─────────────────────────────────────────────── */
function formatDate(value) {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' });
}

function formatDateShort(value) {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString('id-ID', { day: '2-digit', month: 'short', year: 'numeric' });
}

function formatTime(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' });
}

function formatCurrency(value) {
  if (value === null || value === undefined || value === '') return '-';
  return new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(Number(value || 0) || 0);
}

function normalizeStatus(status) {
  return String(status || '').toLowerCase().trim();
}

function resolveStatus(status) {
  const statusMap = {
    pending:    { label: 'Menunggu',   color: 'amber',   icon: FiClock },
    verifikasi: { label: 'Verifikasi', color: 'blue',    icon: FiActivity },
    disetujui:  { label: 'Disetujui', color: 'emerald',  icon: FiCheckCircle },
    approved:   { label: 'Disetujui', color: 'emerald',  icon: FiCheckCircle },
    ditolak:    { label: 'Ditolak',   color: 'red',      icon: FiXCircle },
    rejected:   { label: 'Ditolak',   color: 'red',      icon: FiXCircle },
    selesai:    { label: 'Selesai',   color: 'indigo',   icon: FiActivity },
    completed:  { label: 'Selesai',   color: 'indigo',   icon: FiActivity },
  };
  return statusMap[normalizeStatus(status)] || { label: String(status || 'Tidak diketahui'), color: 'slate', icon: FiHash };
}

function normalizeId(value) {
  return String(value ?? '').trim();
}

function getDataPengajuanId(item) {
  return normalizeId(item?.id || item?.id_data_pengajuan || item?.dataPengajuan?.id);
}

function getProgramName(item) {
  return item?.program?.nama_program || item?.nama_program || '-';
}

function getLembagaName(item) {
  return item?.lembaga?.nama_lembaga || item?.nama_lembaga || '-';
}

function getFileUrl(value) {
  const rawValue = String(value || '').trim();
  if (!rawValue) return '';
  if (rawValue.startsWith('data:image/')) return rawValue;
  return buildFileUrl(rawValue);
}

/* ─── Status Badge ─────────────────────────────────────────── */
const statusColorMap = {
  amber:   { bg: 'bg-amber-50',   text: 'text-amber-700',   border: 'border-amber-200',   dot: 'bg-amber-400',   ring: 'ring-amber-100',  glow: 'shadow-amber-100'  },
  blue:    { bg: 'bg-blue-50',    text: 'text-blue-700',    border: 'border-blue-200',    dot: 'bg-blue-500',    ring: 'ring-blue-100',   glow: 'shadow-blue-100'   },
  emerald: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', dot: 'bg-emerald-500', ring: 'ring-emerald-100',glow: 'shadow-emerald-100'},
  red:     { bg: 'bg-red-50',     text: 'text-red-700',     border: 'border-red-200',     dot: 'bg-red-500',     ring: 'ring-red-100',    glow: 'shadow-red-100'    },
  indigo:  { bg: 'bg-indigo-50',  text: 'text-indigo-700',  border: 'border-indigo-200',  dot: 'bg-indigo-500',  ring: 'ring-indigo-100', glow: 'shadow-indigo-100' },
  slate:   { bg: 'bg-slate-50',   text: 'text-slate-600',   border: 'border-slate-200',   dot: 'bg-slate-400',   ring: 'ring-slate-100',  glow: 'shadow-slate-100'  },
};

function StatusBadge({ status, size = 'sm' }) {
  const { label, color, icon: Icon } = resolveStatus(status);
  const c = statusColorMap[color] || statusColorMap.slate;
  const sizeClass = size === 'sm' ? 'px-2.5 py-1 text-xs' : 'px-4 py-1.5 text-sm';

  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border font-semibold ${sizeClass} ${c.bg} ${c.text} ${c.border}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${c.dot}`} />
      {label}
    </span>
  );
}

/* ─── Info Card ────────────────────────────────────────────── */
function InfoCard({ icon: Icon, label, value, accent = false }) {
  return (
    <div className={`group relative overflow-hidden rounded-2xl border p-4 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg ${accent ? 'border-blue-200 bg-gradient-to-br from-blue-50 to-white' : 'border-slate-200 bg-white hover:border-slate-300'}`}>
      <div className={`mb-3 inline-flex h-9 w-9 items-center justify-center rounded-xl ${accent ? 'bg-blue-100' : 'bg-slate-100'}`}>
        <Icon className={`h-4 w-4 ${accent ? 'text-blue-600' : 'text-slate-500'}`} />
      </div>
      <p className="mb-1 text-[10px] font-bold uppercase tracking-widest text-slate-400">{label}</p>
      <div className={`text-sm font-semibold leading-snug ${accent ? 'text-blue-800' : 'text-slate-800'}`}>{value || '-'}</div>
    </div>
  );
}

/* ─── Timeline Item ─────────────────────────────────────────── */
function TimelineItem({ item, isLast, isEditing, editValues, setEditValues, onStartEdit, onSaveEdit, onCancelEdit, loadingUpdate, statusOptions, adminMitraRecords }) {
  const { label, color, icon: Icon } = resolveStatus(item?.status);
  const c = statusColorMap[color] || statusColorMap.slate;
  const rowKey = item?.id_pengajuan || item?.id;
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="relative flex gap-4 pb-6">
      {/* Vertical line */}
      {!isLast && (
        <div className="absolute left-5 top-10 bottom-0 w-0.5 bg-gradient-to-b from-slate-200 to-transparent" />
      )}

      {/* Icon circle */}
      <div className={`relative z-10 mt-1 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full border-2 shadow-sm ${c.bg} ${c.border}`}>
        <Icon className={`h-4 w-4 ${c.text}`} />
      </div>

      {/* Content card */}
      <div className="flex-1 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition-all duration-200 hover:shadow-md">
        {/* Card header */}
        <div className={`flex items-center justify-between gap-3 border-b border-slate-100 px-4 py-3 ${c.bg}`}>
          <div className="flex items-center gap-2 min-w-0">
            <StatusBadge status={item?.status} />
            <span className="truncate text-xs text-slate-500 font-mono">#{item?.id_pengajuan || item?.id || '-'}</span>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <div className="text-right">
              <p className="text-xs font-semibold text-slate-700">{formatDateShort(item?.created_at || item?.createdAt)}</p>
              <p className="text-[10px] text-slate-400">{formatTime(item?.created_at || item?.createdAt)}</p>
            </div>
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="rounded-lg border border-slate-200 bg-white p-1.5 text-slate-500 transition hover:bg-slate-100"
              title={expanded ? 'Tutup detail' : 'Lihat detail'}
            >
              {expanded ? <FiChevronUp className="h-3.5 w-3.5" /> : <FiChevronDown className="h-3.5 w-3.5" />}
            </button>
          </div>
        </div>

        {/* Summary row */}
        <div className="flex flex-wrap items-center gap-x-6 gap-y-2 px-4 py-3">
          <span className="flex items-center gap-1.5 text-sm text-slate-700">
            <FiActivity className="h-3.5 w-3.5 text-slate-400" />
            <span className="font-medium">{getProgramName(item)}</span>
          </span>
          <span className="flex items-center gap-1.5 text-sm text-slate-500">
            <FiMapPin className="h-3.5 w-3.5 text-slate-400" />
            {getLembagaName(item)}
          </span>
          {item?.sumber && (
            <span className="flex items-center gap-1.5 text-xs text-slate-400">
              <FiHash className="h-3 w-3" />
              {item.sumber}
            </span>
          )}
        </div>

        {/* Expanded detail + edit */}
        {expanded && (
          <div className="border-t border-slate-100 px-4 pb-4 pt-3">
            {isEditing ? (
              <div className="space-y-3">
                <p className="text-xs font-bold uppercase tracking-widest text-slate-400">Edit Riwayat</p>
                <div className="grid gap-3 sm:grid-cols-2">
                  <div>
                    <label className="mb-1 block text-xs font-semibold text-slate-600">Status</label>
                    <select
                      value={editValues.status}
                      onChange={(e) => setEditValues((s) => ({ ...s, status: e.target.value }))}
                      className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition"
                    >
                      <option value="">— Pilih status —</option>
                      {statusOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-semibold text-slate-600">Penanggungjawab</label>
                    <select
                      value={editValues.id_user}
                      onChange={(e) => setEditValues((s) => ({ ...s, id_user: e.target.value }))}
                      className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition"
                    >
                      <option value="">— Pilih user —</option>
                      {adminMitraRecords.map((u) => (
                        <option key={String(u?.id || u?.id_user || u?.idUser)} value={String(u?.id || u?.id_user || u?.idUser)}>
                          {u?.nama || u?.name || u?.username || `User ${u?.id || u?.id_user || u?.idUser}`}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="flex gap-2 pt-1">
                  <button
                    type="button"
                    onClick={() => onSaveEdit(item)}
                    disabled={loadingUpdate}
                    className="inline-flex items-center gap-1.5 rounded-xl bg-emerald-600 px-4 py-2 text-xs font-bold text-white shadow-sm transition hover:bg-emerald-700 disabled:opacity-60"
                  >
                    <FiSave className="h-3.5 w-3.5" />
                    {loadingUpdate ? 'Menyimpan...' : 'Simpan'}
                  </button>
                  <button
                    type="button"
                    onClick={onCancelEdit}
                    className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-4 py-2 text-xs font-semibold text-slate-600 transition hover:bg-slate-50"
                  >
                    <FiX className="h-3.5 w-3.5" />
                    Batal
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between gap-2">
                <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs">
                  {item?.user && (
                    <span className="flex items-center gap-1.5 text-slate-600">
                      <FiUser className="h-3 w-3 text-slate-400" />
                      {item.user?.nama || item.user?.name || item.user?.username || '-'}
                    </span>
                  )}
                  <span className="flex items-center gap-1.5 text-slate-500">
                    <FiCalendar className="h-3 w-3 text-slate-400" />
                    {formatDate(item?.updated_at || item?.updatedAt)}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => onStartEdit(item)}
                  className="inline-flex items-center gap-1.5 rounded-xl border border-blue-200 bg-blue-50 px-3 py-1.5 text-xs font-bold text-blue-700 transition hover:bg-blue-100"
                >
                  <FiEdit3 className="h-3 w-3" />
                  Edit
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/* ─── Media Card ────────────────────────────────────────────── */
function MediaCard({ item, getTitle, getSubtitle }) {
  const fileUrl = getFileUrl(item?.file_path);
  const [imgError, setImgError] = useState(false);

  return (
    <div className="group overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg">
      <div className="relative overflow-hidden bg-slate-50" style={{ height: '160px' }}>
        {fileUrl && !imgError ? (
          <img
            src={fileUrl}
            alt={getTitle(item)}
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
            onError={() => setImgError(true)}
          />
        ) : (
          <div className="flex h-full items-center justify-center flex-col gap-2">
            <FiImage className="h-8 w-8 text-slate-300" />
            <p className="text-xs text-slate-400">Tidak tersedia</p>
          </div>
        )}
        {fileUrl && !imgError && (
          <a
            href={fileUrl}
            target="_blank"
            rel="noreferrer"
            className="absolute inset-0 flex items-center justify-center bg-black/0 opacity-0 transition-all duration-200 group-hover:bg-black/30 group-hover:opacity-100"
          >
            <span className="inline-flex items-center gap-2 rounded-xl bg-white/90 px-3 py-1.5 text-xs font-bold text-slate-800 backdrop-blur-sm shadow">
              <FiExternalLink className="h-3.5 w-3.5" />
              Buka file
            </span>
          </a>
        )}
      </div>
      <div className="p-3">
        <p className="truncate text-sm font-semibold text-slate-800">{getTitle(item)}</p>
        <p className="mt-0.5 truncate text-xs text-slate-500">{getSubtitle(item)}</p>
      </div>
    </div>
  );
}

/* ─── Media Section ─────────────────────────────────────────── */
function MediaSection({ title, icon: Icon, items, getTitle, getSubtitle }) {
  const [open, setOpen] = useState(true);

  return (
    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-3 border-b border-slate-100 bg-gradient-to-r from-slate-50 to-white px-5 py-4 transition hover:bg-slate-50/70"
      >
        <div className="flex items-center gap-2.5">
          <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-blue-100">
            <Icon className="h-3.5 w-3.5 text-blue-600" />
          </div>
          <h3 className="text-sm font-bold text-slate-900">{title}</h3>
          <span className="rounded-full border border-blue-200 bg-blue-50 px-2.5 py-0.5 text-xs font-bold text-blue-700">
            {items.length}
          </span>
        </div>
        {open ? <FiChevronUp className="h-4 w-4 text-slate-400" /> : <FiChevronDown className="h-4 w-4 text-slate-400" />}
      </button>

      {open && (
        <div className="p-4">
          {items.length === 0 ? (
            <div className="flex flex-col items-center gap-2 rounded-xl border border-dashed border-slate-200 bg-slate-50/60 py-8">
              <Icon className="h-8 w-8 text-slate-300" />
              <p className="text-sm text-slate-400">Belum ada data</p>
            </div>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2">
              {items.map((item, index) => (
                <MediaCard
                  key={item?.id_dokumen || item?.id_foto || `${title}-${index}`}
                  item={item}
                  getTitle={getTitle}
                  getSubtitle={getSubtitle}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ─── Skeleton Loader ────────────────────────────────────────── */
function SkeletonBlock({ className = '' }) {
  return <div className={`animate-pulse rounded-xl bg-slate-100 ${className}`} />;
}

function LoadingSkeleton() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/30">
      <div className="mx-auto max-w-7xl space-y-6 px-4 py-8 sm:px-6 lg:px-10">
        <SkeletonBlock className="h-28 w-full" />
        <div className="grid gap-4 sm:grid-cols-3">
          <SkeletonBlock className="h-20" />
          <SkeletonBlock className="h-20" />
          <SkeletonBlock className="h-20" />
        </div>
        <div className="grid gap-6 xl:grid-cols-[1.4fr_0.6fr]">
          <div className="space-y-4">
            <SkeletonBlock className="h-48" />
            <SkeletonBlock className="h-80" />
          </div>
          <div className="space-y-4">
            <SkeletonBlock className="h-48" />
            <SkeletonBlock className="h-48" />
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── Main Component ─────────────────────────────────────────── */
function RiwayatPengajuanPage({ admin }) {
  const { id } = useParams();
  const navigate = useNavigate();

  const [dataPengajuan, setDataPengajuan] = useState(null);
  const [dokumen, setDokumen] = useState([]);
  const [foto, setFoto] = useState([]);
  const [jenisFotoMap, setJenisFotoMap] = useState(new Map());
  const [loadingDetails, setLoadingDetails] = useState(true);
  const [error, setError] = useState(null);
  const [adminMitraRecords, setAdminMitraRecords] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editValues, setEditValues] = useState({ status: '', id_user: '' });
  const [filterStatus, setFilterStatus] = useState('all');


  const { data: riwayatData, loading: loadingRiwayat, error: riwayatError, fetchById: fetchRiwayatById } = useGetRiwayatPengajuan();
  const lembagaScope = getAdminLembagaScope(admin);
  const { updateRiwayatPengajuan, loading: loadingUpdate, error: updateError } = useUpdateRiwayatPengajuan();

  const extractDataPayload = (payload, keys) => {
    if (!payload) return [];
    if (Array.isArray(payload)) return payload;
    for (const key of keys) {
      if (payload?.[key]) return payload[key];
    }
    if (payload.data) return payload.data;
    return [payload];
  };

  const buildJenisFotoMap = (payload) => {
    const items = extractDataPayload(payload, ['jenis_foto', 'jenisFoto', 'data']);
    const map = new Map();
    items.forEach((item) => {
      const key = normalizeId(item?.id_jenis_foto || item?.id);
      if (key) map.set(key, item);
    });
    return map;
  };

  useEffect(() => {
    let cancelled = false;

    const fetchData = async () => {
      setLoadingDetails(true);
      setError(null);
      try {
        const [responseData, responseDokumen, responseFoto, responseJenisFoto, responseRiwayat] = await Promise.all([
          api.get(endpointsAdmin.data_pengajuan.getById(id)),
          api.get(endpointsAdmin.dokumen_pengajuan.getAll),
          api.get(endpointsAdmin.tbl_foto.getAll),
          api.get(endpointsAdmin.jenis_foto.getAll),
          fetchRiwayatById(id),
        ]);
        if (cancelled) return;
        setDataPengajuan(extractDataPayload(responseData.data, ['data_pengajuan', 'dataPengajuan', 'pengajuan', 'data']));
        setDokumen(extractDataPayload(responseDokumen.data, ['dokumen_pengajuan', 'dokumenPengajuan', 'dokumen', 'data']));
        setFoto(extractDataPayload(responseFoto.data, ['foto_pengajuan', 'tbl_foto', 'tblFoto', 'foto', 'data']));
        setJenisFotoMap(buildJenisFotoMap(responseJenisFoto.data));
      } catch (err) {
        if (cancelled) return;
        setError(err.response?.data?.message || err.response?.data?.error || err.message || 'Gagal memuat data pengajuan');
      } finally {
        if (!cancelled) setLoadingDetails(false);
      }
    };

    fetchData();

    const fetchAdminMitra = async () => {
      if (!lembagaScope?.active) return setAdminMitraRecords([]);
      try {
        const resp = await api.post(endpointsAdmin.adminmitra.createByLembaga(lembagaScope.id), {});
        if (cancelled) return;
        const users = extractDataPayload(resp.data, ['adminmitra', 'data_adminmitra', 'users']);
        setAdminMitraRecords(Array.isArray(users) ? users : []);
      } catch {
        if (!cancelled) setAdminMitraRecords([]);
      }
    };

    fetchAdminMitra();

    return () => { cancelled = true; };
  }, [id, fetchRiwayatById]);

  const statusOptions = [
    { value: 'pending',    label: 'Menunggu' },
    { value: 'verifikasi', label: 'Verifikasi' },
    { value: 'disetujui',  label: 'Disetujui' },
    { value: 'approved',   label: 'Disetujui (approved)' },
    { value: 'ditolak',    label: 'Ditolak' },
    { value: 'rejected',   label: 'Ditolak (rejected)' },
    { value: 'selesai',    label: 'Selesai' },
    { value: 'completed',  label: 'Selesai (completed)' },
  ];

  const startEdit = (item) => {
    setEditingId(item?.id_pengajuan || item?.id || null);
    setEditValues({
      status:  normalizeStatus(item?.status) || '',
      id_user: String(findFieldDeep(item, ['id_user', 'idUser']) || findFieldDeep(item?.user, ['id_user', 'idUser', 'id']) || ''),
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditValues({ status: '', id_user: '' });
  };

  const saveEdit = async (item) => {
    const idPengajuan = normalizeId(item?.id_pengajuan || item?.id || '');
    if (!idPengajuan) return;
    const payload = {};
    if (editValues.status)  payload.status  = editValues.status;
    if (editValues.id_user) payload.id_user = editValues.id_user;
    const result = await updateRiwayatPengajuan(idPengajuan, payload);
    if (result) {
      await fetchRiwayatById(id);
      cancelEdit();
    }
  };

  const getSelectedDetail = useMemo(() => {
    if (!dataPengajuan) return { dokumen: [], foto: [], riwayat: [] };
    const dataId = getDataPengajuanId(dataPengajuan);
    const selectedDokumen = dokumen.filter((item) => normalizeId(item?.id_data_pengajuan || item?.dataPengajuan?.id) === dataId);
    const selectedFoto    = foto.filter((item)    => normalizeId(item?.id_data_pengajuan || item?.dataPengajuan?.id) === dataId);
    const selectedRiwayat = extractDataPayload(riwayatData, ['riwayat_pengajuan', 'riwayatPengajuan', 'pengajuan', 'data'])
      .filter((item) => {
        const matchesId = normalizeId(item?.id_data_pengajuan || item?.dataPengajuan?.id) === dataId || normalizeId(item?.id_pengajuan) === dataId;
        if (!matchesId) return false;
        return matchItemToAdminScope(item, lembagaScope);
      });
    return { dokumen: selectedDokumen, foto: selectedFoto, riwayat: selectedRiwayat };
  }, [dataPengajuan, dokumen, foto, riwayatData, lembagaScope]);

  const filteredRiwayat = useMemo(() => {
    if (filterStatus === 'all') return getSelectedDetail.riwayat;
    return getSelectedDetail.riwayat.filter((item) => normalizeStatus(item?.status) === filterStatus);
  }, [getSelectedDetail.riwayat, filterStatus]);

  /* Stats */
  const stats = useMemo(() => {
    const riwayat = getSelectedDetail.riwayat;
    const countByStatus = (s) => riwayat.filter((r) => normalizeStatus(r?.status) === s).length;
    return {
      total:   riwayat.length,
      pending: countByStatus('pending'),
      disetujui: riwayat.filter((r) => ['disetujui','approved'].includes(normalizeStatus(r?.status))).length,
      ditolak:   riwayat.filter((r) => ['ditolak','rejected'].includes(normalizeStatus(r?.status))).length,
    };
  }, [getSelectedDetail.riwayat]);

  /* ── Loading ── */
  if (loadingDetails || loadingRiwayat) return <LoadingSkeleton />;

  /* ── Error ── */
  if (error || riwayatError) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-50 via-white to-red-50/30 px-6">
        <div className="w-full max-w-md overflow-hidden rounded-3xl border border-red-200 bg-white shadow-xl">
          <div className="h-1.5 bg-gradient-to-r from-red-500 to-rose-400" />
          <div className="p-8 text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
              <FiAlertCircle className="h-8 w-8 text-red-500" />
            </div>
            <h2 className="mb-2 text-lg font-bold text-slate-900">Terjadi Kesalahan</h2>
            <p className="mb-6 text-sm text-slate-500">{error || riwayatError}</p>
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-slate-700"
            >
              <FiArrowLeft className="h-4 w-4" />
              Kembali
            </button>
          </div>
        </div>
      </div>
    );
  }

  const latestStatus = getSelectedDetail.riwayat[getSelectedDetail.riwayat.length - 1]?.status || dataPengajuan?.status;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/40">
      <div className="mx-auto max-w-7xl space-y-6 px-4 py-8 sm:px-6 lg:px-10">

        {/* ── Header ── */}
        <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
          <div className="h-1 bg-gradient-to-r from-blue-900 via-blue-600 to-indigo-500" />
          <div className="flex flex-col gap-4 px-6 py-5 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-4">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs font-semibold text-slate-700 transition hover:bg-slate-100 hover:shadow-sm"
              >
                <FiArrowLeft className="h-3.5 w-3.5" />
                Kembali
              </button>
              <div>
                <p className="text-[10px] font-bold uppercase tracking-widest text-blue-600">Admin Panel</p>
                <h1 className="text-xl font-bold text-slate-900 sm:text-2xl">Riwayat Pengajuan</h1>
                <p className="mt-0.5 text-xs text-slate-500">Detail dan riwayat lengkap pengajuan</p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {latestStatus && <StatusBadge status={latestStatus} size="md" />}
              <span className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-semibold text-slate-600">
                <FiHash className="h-3 w-3" />
                {id}
              </span>
            </div>
          </div>
        </div>

        {/* ── Stats Row ── */}
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {[
            {
              label: 'Total Riwayat', value: stats.total,      icon: MdOutlineTimeline,
              wrapCls: 'border-blue-200 bg-gradient-to-br from-blue-50 to-white',
              iconCls: 'bg-blue-100', textCls: 'text-blue-600',
            },
            {
              label: 'Menunggu',      value: stats.pending,    icon: FiClock,
              wrapCls: 'border-amber-200 bg-gradient-to-br from-amber-50 to-white',
              iconCls: 'bg-amber-100', textCls: 'text-amber-600',
            },
            {
              label: 'Disetujui',     value: stats.disetujui,  icon: FiCheckCircle,
              wrapCls: 'border-emerald-200 bg-gradient-to-br from-emerald-50 to-white',
              iconCls: 'bg-emerald-100', textCls: 'text-emerald-600',
            },
            {
              label: 'Ditolak',       value: stats.ditolak,    icon: FiXCircle,
              wrapCls: 'border-red-200 bg-gradient-to-br from-red-50 to-white',
              iconCls: 'bg-red-100', textCls: 'text-red-600',
            },
          ].map(({ label, value, icon: Icon, wrapCls, iconCls, textCls }) => (
            <div key={label} className={`overflow-hidden rounded-2xl border p-4 ${wrapCls}`}>
              <div className={`mb-2 inline-flex h-8 w-8 items-center justify-center rounded-xl ${iconCls}`}>
                <Icon className={`h-4 w-4 ${textCls}`} />
              </div>
              <p className="text-2xl font-bold text-slate-900">{value}</p>
              <p className="text-xs text-slate-500">{label}</p>
            </div>
          ))}
        </div>

        {/* ── Main Content Grid ── */}
        <div className="grid gap-6 xl:grid-cols-[1.4fr_0.6fr]">

          {/* LEFT: Data Pengajuan + Riwayat Timeline */}
          <div className="space-y-5">

            {/* Data Pengajuan Card */}
            <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
              <div className="flex items-center justify-between gap-3 border-b border-slate-100 px-5 py-4">
                <div className="flex items-center gap-2.5">
                  <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-indigo-100">
                    <FiFileText className="h-4 w-4 text-indigo-600" />
                  </div>
                  <h2 className="text-sm font-bold text-slate-900">Data Pengajuan</h2>
                </div>
                <StatusBadge status={dataPengajuan?.status} />
              </div>
              <div className="grid gap-3 p-5 sm:grid-cols-2 lg:grid-cols-3">
                <InfoCard icon={FiHash}       label="ID Pengajuan"  value={getDataPengajuanId(dataPengajuan)} accent />
                <InfoCard icon={FiActivity}   label="Program"       value={getProgramName(dataPengajuan)} />
                <InfoCard icon={FiMapPin}     label="Lembaga"       value={getLembagaName(dataPengajuan)} />
                <InfoCard icon={FiDollarSign} label="Nominal"       value={formatCurrency(dataPengajuan?.nominalPengajuan || dataPengajuan?.nominal_pengajuan)} />
                <InfoCard icon={FiCalendar}   label="Tanggal Input" value={formatDate(dataPengajuan?.createdAt || dataPengajuan?.created_at)} />
                <InfoCard icon={FiClock}      label="Status Terkini" value={resolveStatus(dataPengajuan?.status).label} />
              </div>
            </div>

            {/* Timeline Section */}
            <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
              {/* Tab header */}
              <div className="flex items-center justify-between gap-3 border-b border-slate-100 px-5 py-4">
                <div className="flex items-center gap-2.5">
                  <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-blue-100">
                    <MdOutlineTimeline className="h-4 w-4 text-blue-600" />
                  </div>
                  <h2 className="text-sm font-bold text-slate-900">Timeline Riwayat</h2>
                  <span className="rounded-full border border-blue-200 bg-blue-50 px-2.5 py-0.5 text-xs font-bold text-blue-700">
                    {filteredRiwayat.length}
                  </span>
                </div>

                {/* Filter */}
                <div className="flex items-center gap-2">
                  <FiFilter className="h-3.5 w-3.5 text-slate-400" />
                  <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-semibold text-slate-700 outline-none focus:border-blue-300 focus:ring-2 focus:ring-blue-100 transition"
                  >
                    <option value="all">Semua Status</option>
                    {statusOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="p-5">
                {filteredRiwayat.length === 0 ? (
                  <div className="flex flex-col items-center gap-3 rounded-2xl border border-dashed border-slate-200 bg-slate-50/60 py-12">
                    <MdOutlineTimeline className="h-10 w-10 text-slate-300" />
                    <p className="text-sm font-medium text-slate-500">Belum ada riwayat untuk pengajuan ini</p>
                    {filterStatus !== 'all' && (
                      <button
                        type="button"
                        onClick={() => setFilterStatus('all')}
                        className="text-xs font-semibold text-blue-600 underline"
                      >
                        Tampilkan semua
                      </button>
                    )}
                  </div>
                ) : (
                  <div>
                    {filteredRiwayat.map((item, idx) => {
                      const rowKey = item?.id_pengajuan || item?.id || idx;
                      const isEditing = editingId && String(editingId) === String(item?.id_pengajuan || item?.id);
                      return (
                        <TimelineItem
                          key={rowKey}
                          item={item}
                          isLast={idx === filteredRiwayat.length - 1}
                          isEditing={isEditing}
                          editValues={editValues}
                          setEditValues={setEditValues}
                          onStartEdit={startEdit}
                          onSaveEdit={saveEdit}
                          onCancelEdit={cancelEdit}
                          loadingUpdate={loadingUpdate}
                          statusOptions={statusOptions}
                          adminMitraRecords={adminMitraRecords}
                        />
                      );
                    })}
                  </div>
                )}

                {updateError && (
                  <div className="mt-3 flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                    <FiAlertCircle className="h-4 w-4 flex-shrink-0" />
                    {updateError}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* RIGHT: Dokumen & Foto */}
          <div className="space-y-5">
            <MediaSection
              title="Dokumen Pengajuan"
              icon={FiFileText}
              items={getSelectedDetail.dokumen}
              getTitle={(item) => item?.jenisDokumen?.nama || item?.nama_jenis_dokumen || `Dokumen #${item?.id_dokumen || '-'}`}
              getSubtitle={(item) => item?.jenisDokumen?.keterangan || `Diunggah ${formatDate(item?.uploaded_at)}`}
            />

            <MediaSection
              title="Foto Survey"
              icon={FiImage}
              items={getSelectedDetail.foto}
              getTitle={(item) => {
                const jenisFoto = item?.jenisFoto || jenisFotoMap.get(normalizeId(item?.id_jenis_foto));
                return jenisFoto?.nama || `Foto #${item?.id_foto || '-'}`;
              }}
              getSubtitle={(item) => {
                const jenisFoto = item?.jenisFoto || jenisFotoMap.get(normalizeId(item?.id_jenis_foto));
                return jenisFoto?.keterangan || `Valid: ${item?.is_valid ? 'Ya' : 'Tidak'}`;
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default RiwayatPengajuanPage;
